import { apiClient } from './apiClient';
import { EmployeeFormData } from '../components/EmployeeForm';
import { Role } from './roleService';

export interface Contact {
  id: string;
  email_work?: string;
  email_personal?: string;
  emailWork?: string;
  emailPersonal?: string;
  phone_mobile?: string;
  phoneMobile?: string;
  phone_home?: string;
  phoneAlternate?: string;
  phone_alternate?: string;
  phoneNotes?: string;
  phone_notes?: string;
  address_line1?: string;
  addressLine1?: string;
  address_line2?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  zipCode?: string;
  country?: string;
  preferred_contact_method?: 'phone' | 'email' | 'text';
  emergency_contact_name?: string;
  emergency_contact_relation?: string;
  emergency_contact_phone?: string;
  emergencyContactFirstName?: string;
  emergencyContactLastName?: string;
  emergencyContactPhone?: string;
  employee_id: string;
}

export interface Employee {
  id: string;
  employeeId: string;
  email: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  searchName?: string;
  salutation?: string;
  middleInitial?: string;
  suffix?: string;
  initials?: string;
  ssnLast4?: string;
  faceFeatures?: {},
  gender?: 'male' | 'female' | 'nonbinary' | 'prefer_not_to_say';
  dateOfBirth?: string;
  hireDate?: string;
  terminationDate?: string;
  department?: string;
  category?: string;
  activeAt?: string;
  homeSite?: string;
  payCode?: string;
  hoursPerDay?: number;
  hoursPerWeek?: number;
  overtimeRule?: string;
  drbEmployeeNumber?: string;
  status?: 'active' | 'inactive' | 'terminated' | 'on_leave';
  photoUrl?: string;
  phone?: string;
  salary?: number;
  emergencyContact?: string;
  emergencyPhone?: string;
  address?: string;
  managerId?: string;
  manager?: Employee;
  createdAt: string;
  updatedAt: string;
  contacts: Contact[];
  roles?: Role[];
}

export interface ManagerOption {
  id: string;
  employeeId: string;
  drbEmployeeNumber?: string;
  firstName: string;
  lastName: string;
}

function toFormDataValue(value: unknown): string | Blob | null {
  if (value instanceof Blob) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return null;
}

class EmployeeService {
  async getAllEmployees(query?: any): Promise<Employee[]> {
    const response = await apiClient.get('/employees', { params: query });
    return response.data;
  }

  async getEmployee(id: string): Promise<Employee> {
    const response = await apiClient.get(`/employees/${id}`);
    return response.data;
  }

  async createEmployee(employeeData: EmployeeFormData, employeePhoto: File | null): Promise<Employee> {
    // Filter out empty contact objects
    if (employeeData.contacts && Array.isArray(employeeData.contacts)) {
      employeeData.contacts = employeeData.contacts.filter(contact => {
        // Remove contact if all fields are empty or falsy
        return Object.values(contact).some(value => value && value !== '');
      });
    }
    
    // Remove empty managerId to prevent validation errors
    if (!employeeData.managerId || employeeData.managerId === '') {
      delete employeeData.managerId;
    }    
    
    console.log('Sending employee data to backend:', employeeData);

    const formData = new FormData();
    Object.entries(employeeData).forEach(([key, value]) => {
      if (key !== 'contacts' && value) {
        const serialized = toFormDataValue(value);
        if (serialized !== null) {
          formData.append(key, serialized);
        }
      }
    });

    if (employeeData.contacts) {
      employeeData.contacts.forEach((contact, i) => {
        Object.entries(contact).forEach(([key, value]) => {
          if (value) {
            const serialized = toFormDataValue(value);
            if (serialized !== null) {
              formData.append(`contacts[${i}][${key}]`, serialized);
            }
          }
        });
      });
    }
    if (employeePhoto) {
      formData.append('photo', employeePhoto);
    }
    const response = await apiClient.postFormData('/employees', formData);
    const employee = response.data;
    console.log('Received employee response from backend:', employee);
    return employee;
  }

  async updateEmployee(id: string, employeeData: EmployeeFormData, employeePhoto: File | null): Promise<Employee> {
    const { password, ...employeeDataWithoutPassword } = employeeData;
    
    // Filter out empty contact objects
    if (employeeDataWithoutPassword.contacts && Array.isArray(employeeDataWithoutPassword.contacts)) {
      employeeDataWithoutPassword.contacts = employeeDataWithoutPassword.contacts.filter(contact => {
        // Remove contact if all fields are empty or falsy
        return Object.values(contact).some(value => value && value !== '');
      });
    }
    
    // Remove empty managerId to prevent validation errors
    if (!employeeDataWithoutPassword.managerId || employeeDataWithoutPassword.managerId === '') {
      delete employeeDataWithoutPassword.managerId;
    }
    
    // Ensure we only send the fields expected by the backend DTO
    const cleanEmployeeData: Record<string, unknown> = {
      firstName: employeeDataWithoutPassword.firstName,
      lastName: employeeDataWithoutPassword.lastName,
      preferredName: employeeDataWithoutPassword.preferredName,
      searchName: employeeDataWithoutPassword.searchName,
      salutation: employeeDataWithoutPassword.salutation,
      middleInitial: employeeDataWithoutPassword.middleInitial,
      suffix: employeeDataWithoutPassword.suffix,
      initials: employeeDataWithoutPassword.initials,
      ssnLast4: employeeDataWithoutPassword.ssnLast4,
      gender: employeeDataWithoutPassword.gender,
      dateOfBirth: employeeDataWithoutPassword.dateOfBirth,
      hireDate: employeeDataWithoutPassword.hireDate,
      terminationDate: employeeDataWithoutPassword.terminationDate,
      department: employeeDataWithoutPassword.department,
      category: employeeDataWithoutPassword.category,
      activeAt: employeeDataWithoutPassword.activeAt,
      homeSite: employeeDataWithoutPassword.homeSite,
      payCode: employeeDataWithoutPassword.payCode,
      hoursPerDay: employeeDataWithoutPassword.hoursPerDay,
      hoursPerWeek: employeeDataWithoutPassword.hoursPerWeek,
      overtimeRule: employeeDataWithoutPassword.overtimeRule,
      drbEmployeeNumber: employeeDataWithoutPassword.drbEmployeeNumber,
      status: employeeDataWithoutPassword.status,
      photoUrl: employeeDataWithoutPassword.photoUrl,
      biometricData: employeeDataWithoutPassword.biometricData,
      faceFeatures: employeeDataWithoutPassword.faceFeatures,
      mustChangePassword: employeeDataWithoutPassword.mustChangePassword,
      managerId: employeeDataWithoutPassword.managerId || undefined, // Convert empty string to undefined
      roleId: employeeDataWithoutPassword.roleId,
      contacts: employeeDataWithoutPassword.contacts
    };

    if (password && password.trim()) {
      cleanEmployeeData.password = password.trim();
    }
    
    console.log('Sending employee update data to backend:', cleanEmployeeData);
    console.log('Data keys being sent:', Object.keys(cleanEmployeeData));
    console.log('Data values being sent:', cleanEmployeeData);

    const formData = new FormData();
    Object.entries(cleanEmployeeData).forEach(([key, value]) => {
      if (key !== 'contacts' && value) {
        const serialized = toFormDataValue(value);
        if (serialized !== null) {
          formData.append(key, serialized);
        }
      }
    });

    const contacts = employeeDataWithoutPassword.contacts;
    if (contacts) {
      contacts.forEach((contact, i) => {
        Object.entries(contact).forEach(([key, value]) => {
          if (value) {
            const serialized = toFormDataValue(value);
            if (serialized !== null) {
              formData.append(`contacts[${i}][${key}]`, serialized);
            }
          }
        });
      });
    }
    if (employeePhoto) {
      formData.append('photo', employeePhoto);
    }
    
    const response = await apiClient.putFormData(`/employees/${id}`, formData);
    const employee = response.data;
    console.log('Received employee update response from backend:', employee);
    
    return employee;
  }

  async deleteEmployee(id: string): Promise<void> {
    await apiClient.delete(`/employees/${id}`);
  }

  async uploadPhoto(id: string, file: File): Promise<{ photoUrl: string }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      console.log('Uploading photo for employee:', id);
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      const response = await apiClient.post(`/employees/${id}/photo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout
      });
      
      console.log('Upload response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Upload error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw error;
    }
  }

  // Get managers for dropdown (employees who can be managers)
  async getManagers(): Promise<ManagerOption[]> {
    const response = await apiClient.get('/employees', { 
      params: { 
        status: 'active',
        limit: 1000 // Get all active employees for manager selection
      } 
    });
    return response.data.map((emp: Employee) => ({
      id: emp.id,
      employeeId: emp.employeeId,
      drbEmployeeNumber: emp.drbEmployeeNumber,
      firstName: emp.firstName,
      lastName: emp.lastName,
    }));
  }

  // Get roles for dropdown
  async getRoles(): Promise<Role[]> {
    const response = await apiClient.get('/roles');
    return response.data;
  }

  // Get employee roles
  async getEmployeeRoles(employeeId: string): Promise<Role[]> {
    const response = await apiClient.get(`/employees/${employeeId}/roles`);
    return response.data;
  }

  // Assign role to employee
  async assignRole(employeeId: string, roleId: number): Promise<void> {
    await apiClient.post(`/employees/${employeeId}/roles`, { roleId });
  }

  // Remove role from employee
  async removeRole(employeeId: string, roleId: number): Promise<void> {
    await apiClient.delete(`/employees/${employeeId}/roles/${roleId}`);
  }

  // Save employee permissions
  async saveEmployeePermissions(employeeId: string, mappings: Array<{ screenId: number, permissionIds: number[] }>): Promise<any> {
    const response = await apiClient.post(`/employees/${employeeId}/permissions`, { mappings });
    return response.data;
  }
}

export const employeeService = new EmployeeService(); 